import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Check } from "lucide-react";

const STYLES = ["Top-handle", "Tote", "Crescent", "Mini frame", "Bucket"];
const COLORS = [
  { key: "Cognac", hex: "#A0552A" },
  { key: "Onyx", hex: "#0A0A0A" },
  { key: "Bone", hex: "#EFE7D6" },
  { key: "Garnet", hex: "#6E1B22" },
  { key: "Olive", hex: "#5A5D3A" },
  { key: "Sand", hex: "#C4AB85" },
];
const MATERIALS = ["Italian calfskin", "Vegetable-tanned leather", "Box calf", "Pebble grain"];
const SIZES = ["Mini", "Small", "Medium", "Large"];
const HARDWARE = ["Gold", "Silver", "Brushed black"];

const STEPS = [
  { key: "style", label: "Silhouette" },
  { key: "material", label: "Material" },
  { key: "color", label: "Color" },
  { key: "details", label: "Details" },
  { key: "contact", label: "Submit" },
];

export default function CustomBag() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    style: STYLES[0], material: MATERIALS[0], color: "Cognac", size: "Medium",
    hardware: "Gold", initials: "", inspiration_image: "", special_instructions: "",
    contact_name: "", contact_email: "", contact_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImg = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_500_000) { toast.error("Image too large (max 2.5 MB)"); return; }
    const r = new FileReader();
    r.onload = () => set("inspiration_image", r.result);
    r.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.contact_name || !form.contact_email) { toast.error("Name and email are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/custom-bags", form);
      setDone(true);
    } catch { toast.error("Could not submit, please try again."); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="custom-bag-success" className="max-w-2xl mx-auto px-6 py-32 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#C5A059] text-white flex items-center justify-center"><Check size={28} /></div>
        <div className="overline mt-8">Atelier received your request</div>
        <h1 className="serif text-5xl mt-4">Your bag is being sketched.</h1>
        <p className="text-[#5A5A5A] mt-6 leading-relaxed">A bespoke specialist will email you within 24 hours with a quote, swatch options, and an estimated four-week craft window.</p>
      </div>
    );
  }

  return (
    <div data-testid="custom-bag-page" className="max-w-[1300px] mx-auto px-6 lg:px-12 py-16">
      <div className="text-center mb-16">
        <div className="overline text-[#C5A059]">Bespoke Atelier</div>
        <h1 className="serif text-5xl lg:text-7xl mt-3">Design your bag.</h1>
        <p className="text-[#5A5A5A] mt-4 max-w-xl mx-auto">Five quiet steps. Hand-cut and stitched in our atelier. Yours in four weeks.</p>
      </div>

      {/* Stepper */}
      <div className="flex justify-center gap-2 lg:gap-6 mb-14 flex-wrap">
        {STEPS.map((s, i) => (
          <button key={s.key} onClick={() => setStep(i)} className={`flex items-center gap-2 text-[0.65rem] tracking-[0.25em] uppercase ${i === step ? "text-[#0A0A0A]" : "text-[#8A8A8A]"}`}>
            <span className={`w-6 h-6 flex items-center justify-center border ${i === step ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5DF]"}`}>{i + 1}</span>
            <span className="hidden sm:inline">{s.label}</span>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-12">
        {/* Preview / summary */}
        <div className="lg:col-span-2 lg:sticky lg:top-44 self-start">
          <div className="bg-[#EFEFE8] aspect-square-img flex items-center justify-center relative overflow-hidden">
            <img src="https://images.pexels.com/photos/27174573/pexels-photo-27174573.jpeg" alt="Bespoke bag preview" className="w-full h-full object-cover opacity-50" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
              <div className="overline">Your Bespoke</div>
              <div className="serif text-3xl mt-2">{form.style}</div>
              <div className="text-sm mt-2 text-[#5A5A5A]">{form.material} · {form.color}</div>
              <div className="text-sm text-[#5A5A5A]">Size {form.size} · {form.hardware} hardware</div>
              {form.initials && <div className="serif text-2xl mt-4 text-[#C5A059]">— {form.initials.toUpperCase()} —</div>}
            </div>
          </div>
          <div className="mt-6 text-xs text-[#5A5A5A] leading-relaxed">
            Bespoke pieces start from AED 2,200. Final pricing is shared via personal quote after our atelier reviews your selections.
          </div>
        </div>

        {/* Step content */}
        <div className="lg:col-span-3">
          {step === 0 && (
            <div data-testid="step-style">
              <div className="overline">Step one</div>
              <h2 className="serif text-4xl mt-2 mb-8">Choose a silhouette</h2>
              <div className="grid grid-cols-2 gap-3">
                {STYLES.map((s) => (
                  <button key={s} data-testid={`style-${s}`} onClick={() => set("style", s)} className={`text-left p-5 border ${form.style === s ? "border-[#0A0A0A] bg-white" : "border-[#E5E5DF]"}`}>
                    <div className="serif text-xl">{s}</div>
                  </button>
                ))}
              </div>
              <div className="mt-8 fld-label">Size</div>
              <div className="flex gap-2 flex-wrap mt-3">
                {SIZES.map((s) => (
                  <button key={s} data-testid={`size-${s}`} onClick={() => set("size", s)} className={`px-5 py-2.5 text-xs border ${form.size === s ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5DF]"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {step === 1 && (
            <div data-testid="step-material">
              <div className="overline">Step two</div>
              <h2 className="serif text-4xl mt-2 mb-8">Choose a material</h2>
              <div className="grid grid-cols-1 gap-3">
                {MATERIALS.map((m) => (
                  <button key={m} data-testid={`material-${m}`} onClick={() => set("material", m)} className={`text-left p-5 border ${form.material === m ? "border-[#0A0A0A] bg-white" : "border-[#E5E5DF]"}`}>
                    <div className="serif text-xl">{m}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 2 && (
            <div data-testid="step-color">
              <div className="overline">Step three</div>
              <h2 className="serif text-4xl mt-2 mb-8">Choose a color</h2>
              <div className="grid grid-cols-3 gap-3">
                {COLORS.map((c) => (
                  <button key={c.key} data-testid={`color-${c.key}`} onClick={() => set("color", c.key)} className={`p-3 border ${form.color === c.key ? "border-[#0A0A0A]" : "border-[#E5E5DF]"}`}>
                    <div className="aspect-square w-full" style={{ background: c.hex }}></div>
                    <div className="text-xs mt-3 tracking-[0.15em] uppercase">{c.key}</div>
                  </button>
                ))}
              </div>
              <div className="mt-10 fld-label">Hardware</div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {HARDWARE.map((h) => (
                  <button key={h} data-testid={`hardware-${h}`} onClick={() => set("hardware", h)} className={`px-5 py-2.5 text-xs border ${form.hardware === h ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5DF]"}`}>{h}</button>
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div data-testid="step-details">
              <div className="overline">Step four</div>
              <h2 className="serif text-4xl mt-2 mb-8">The personal touches</h2>
              <div className="space-y-8">
                <div>
                  <div className="fld-label">Initials (max 3)</div>
                  <input data-testid="initials-input" maxLength={3} value={form.initials} onChange={(e) => set("initials", e.target.value)} className="fld mt-2 uppercase tracking-[0.3em]" placeholder="A · N · L" />
                </div>
                <div>
                  <div className="fld-label">Inspiration image (optional)</div>
                  <label className="mt-3 flex items-center gap-3 border border-dashed border-[#E5E5DF] p-6 cursor-pointer hover:border-[#0A0A0A]">
                    <Upload size={16} />
                    <span className="text-sm">{form.inspiration_image ? "Image uploaded — replace" : "Upload an image"}</span>
                    <input type="file" accept="image/*" onChange={uploadImg} className="hidden" data-testid="inspiration-upload" />
                  </label>
                  {form.inspiration_image && <img src={form.inspiration_image} alt="" className="w-32 h-32 object-cover mt-4" />}
                </div>
                <div>
                  <div className="fld-label">Special instructions</div>
                  <textarea data-testid="special-instructions" rows={4} value={form.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} className="fld mt-2 resize-none" placeholder="Anything our atelier should know…" />
                </div>
              </div>
            </div>
          )}
          {step === 4 && (
            <div data-testid="step-contact">
              <div className="overline">Final step</div>
              <h2 className="serif text-4xl mt-2 mb-8">Where to send your quote</h2>
              <div className="space-y-6">
                <div><div className="fld-label">Your name</div><input data-testid="contact-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="fld mt-2" required /></div>
                <div><div className="fld-label">Email</div><input data-testid="contact-email" type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="fld mt-2" required /></div>
                <div><div className="fld-label">Phone (optional)</div><input data-testid="contact-phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="fld mt-2" /></div>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-12 pt-8 border-t border-[#E5E5DF]">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="btn-ghost disabled:opacity-30" data-testid="step-prev">← Back</button>
            {step < STEPS.length - 1
              ? <button onClick={() => setStep((s) => s + 1)} className="btn-primary" data-testid="step-next">Continue</button>
              : <button onClick={submit} disabled={submitting} className="btn-primary" data-testid="step-submit">{submitting ? "Submitting…" : "Request quote"}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
